import { z } from "zod";
import { Id, IsoDateTime } from "./common";

export const UserStatus = z.enum(["Invited", "Active", "Disabled"]);
export type UserStatus = z.infer<typeof UserStatus>;

/**
 * Indicates whether the user belongs to the admin console, field app,
 * customer portal, or the platform-operations audience. Drives which
 * API surfaces and role types are valid for this user.
 */
export const UserAudience = z.enum(["Admin", "FieldUser", "PortalUser", "Platform"]);
export type UserAudience = z.infer<typeof UserAudience>;

/**
 * Affiliation type distinguishes Conatus tenant employees/contractors
 * from third-party service-provider users who act under a
 * ServiceProviderEngagement.
 */
export const AffiliationType = z.enum([
  "Employee",
  "Contractor",
  "ThirdPartyServiceProvider",
]);
export type AffiliationType = z.infer<typeof AffiliationType>;

export const UserProfile = z.object({
  userId: Id,
  cognitoSub: z.string().min(1),
  tenantId: Id,
  email: z.string().email(),
  firstName: z.string().max(80).nullable(),
  lastName: z.string().max(80).nullable(),
  displayName: z.string().min(1).max(160),
  status: UserStatus,
  lastLoginAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  createdBy: Id.nullable(),
  updatedBy: Id.nullable(),
  // End-user extension fields — optional so existing admin user records parse correctly.
  audience: UserAudience.optional(),
  affiliationType: AffiliationType.optional(),
  /** Present for ThirdPartyServiceProvider users; links to their ServiceProviderOrg. */
  serviceProviderOrgId: Id.nullable().optional(),
});
export type UserProfile = z.infer<typeof UserProfile>;

export const UserInviteInput = z.object({
  email: z.string().email(),
  firstName: z.string().max(80).optional(),
  lastName: z.string().max(80).optional(),
  displayName: z.string().min(1).max(160).optional(),
  /** Tenant the new user belongs to. Ignored unless the caller is PlatformAdmin. */
  tenantId: Id.optional(),
  /** Roles to assign on creation. Defaults to `Viewer` if empty. */
  roleKeys: z.array(z.string().min(1)).optional(),
  audience: UserAudience.optional(),
  affiliationType: AffiliationType.optional(),
  serviceProviderOrgId: Id.nullable().optional(),
});
export type UserInviteInput = z.infer<typeof UserInviteInput>;

export const UserUpdateInput = z
  .object({
    firstName: z.string().max(80).nullable(),
    lastName: z.string().max(80).nullable(),
    displayName: z.string().min(1).max(160),
    status: UserStatus,
    audience: UserAudience,
    affiliationType: AffiliationType,
    serviceProviderOrgId: Id.nullable(),
  })
  .partial();
export type UserUpdateInput = z.infer<typeof UserUpdateInput>;
